import i18n from '@i18n';
import { BASE_URL } from '@helpers/api';
import { CHAT_WIDGET_SCRIPT_ID } from '@constants/widget';
import { supportChatService } from '@services';
import type { SupportChatIdentity } from '@services/support-chat';
import logger from '@widgets/logger';
import PostHogManager from '@widgets/posthog-manager';

type Pluspoint = ((command: string, ...args: unknown[]) => void) & { q?: ArrayLike<unknown>[] };

declare global {
    interface Window {
        pluspoint?: Pluspoint;
    }
}

const DESKTOP_MIN_SCREEN_WIDTH = 769;
const IDENTITY_RENEWAL_MS = 6 * 60 * 60 * 1000;
const IDENTITY_RENEWAL_CHECK_MS = 60 * 1000;

const send = (command: string, ...args: unknown[]) => {
    if (!window.pluspoint) {
        const queue: Pluspoint = (...call: unknown[]) => {
            (queue.q = queue.q || []).push(call);
        };
        window.pluspoint = queue;
    }
    window.pluspoint(command, ...args);
};

const isWidgetInjected = () => document.getElementById(CHAT_WIDGET_SCRIPT_ID) !== null;

const loadWidget = (channelId: string) => {
    if (isWidgetInjected()) return;
    const script = document.createElement('script');
    script.id = CHAT_WIDGET_SCRIPT_ID;
    script.dataset.channelid = channelId;
    if (i18n.resolvedLanguage) script.dataset.language = i18n.resolvedLanguage;
    script.src = `${BASE_URL}widget/pl.widget.chat.min.js`;
    script.onerror = () => {
        script.remove();
        delete window.pluspoint;
    };
    document.body.appendChild(script);
};

const settingsFor = async (userId: number | null) => {
    if (userId !== null) {
        const signedIn = await supportChatService.get(true).catch(() => null);
        if (signedIn) return signedIn;
    }
    return supportChatService.get(false);
};

class SupportChatManager {
    private visitor: number | null | undefined;
    private request = 0;
    private identityRenewal: ReturnType<typeof setInterval> | undefined;

    constructor() {
        i18n.on('languageChanged', () => {
            if (isWidgetInjected()) send('setLanguage', i18n.resolvedLanguage);
        });
    }

    async start(userId: number | null): Promise<void> {
        if (userId === this.visitor) return;
        if (typeof this.visitor === 'number') this.forget();
        if (window.screen.width < DESKTOP_MIN_SCREEN_WIDTH) return;
        if (userId !== null && PostHogManager.isImpersonating()) return;
        this.visitor = userId;
        const request = ++this.request;
        try {
            const chat = await settingsFor(userId);
            if (!chat || request !== this.request) return;
            if (userId !== null) this.signIn(String(userId), chat.identity, request);
            loadWidget(chat.channelId);
        } catch (error) {
            logger.warn('Support chat could not be loaded:', error);
        }
    }

    forget(): void {
        if (this.visitor === null) return;
        this.request++;
        clearInterval(this.identityRenewal);
        this.visitor = isWidgetInjected() ? null : undefined;
        send('forget');
    }

    private signIn(customerId: string, identity: SupportChatIdentity | null, request: number) {
        if (identity) send('identify', identity);
        let renewsAt = Date.now() + IDENTITY_RENEWAL_MS;
        const renewal = setInterval(async () => {
            if (!isWidgetInjected()) {
                clearInterval(renewal);
                return;
            }
            if (Date.now() < renewsAt) return;
            renewsAt = Date.now() + IDENTITY_RENEWAL_MS;
            const renewed = await supportChatService.get(true).catch(() => null);
            if (request === this.request && renewed?.identity?.customerId === customerId) {
                send('identify', renewed.identity);
            }
        }, IDENTITY_RENEWAL_CHECK_MS);
        this.identityRenewal = renewal;
    }
}

export default new SupportChatManager();
